<template>
  <div v-if="(isReady && pageType === 'edit') || pageType === 'create'" ref="top">
    <slot name="header">
      <h5 class="font-weight-bold mb-4">Schedule a session</h5>
    </slot>

    <div v-if="isLoading" class="d-flex justify-content-center p-4 m-3">
      <!-- Loading spinner -->
      <b-spinner label="Loading..." variant="dark" />
    </div>

    <div v-else>
      <b-alert show class="d-flex align-items-center pl-2 align-content-center" variant="info">
        <span class="ml-2">{{ cityTimezone }} Time</span>
      </b-alert>

      <b-alert :show="overlappedSections.length > 0" class="my-2" variant="danger">
        The specified section timing overlaps your other sections.
        <div
          class="font-weight-bold online-meetings__show-conflicted-details"
          @click="showModal('modal-alert-overlap-section')"
        >
          Show details
        </div>
        <ModalAlertConflict
          ref="modal-alert-overlap-section"
          conflict-type="overlap-section"
          title="Overlapped Sections"
          sub-title="Sections"
          description="The specified section timing overlaps your other sections."
          :conflict-list="overlappedSections"
          @confirm="submit"
        />
      </b-alert>

      <BaseDatePicker
        :id-date-picker="'start-enrollment-date'"
        :form-old-data="form"
        :form-exist-data="$_section"
        date-picker-class="w-25 mr-2"
        :disabled="hasEnrolledLearner"
        type-date="start_enrollment"
        @emitDate="getStartEnrollmentDate"
      >
        <span slot="title">Start enrollment</span>
        <div
          v-if="!form.start_enrollment && submitFail"
          slot="required-field-date"
          class="small text-danger mt-2"
        >
          The Start enrollment is a required field
        </div>
      </BaseDatePicker>

      <BaseDatePicker
        :id-date-picker="'last-enrollment-date'"
        :form-old-data="form"
        :form-exist-data="$_section"
        date-picker-class="w-25 mr-2"
        :disabled="hasEnrolledLearner"
        type-date="last_enrollment"
        @emitDate="getLastEnrollmentDate"
      >
        <span slot="title">Last enrollment</span>
        <div
          v-if="!form.last_enrollment && submitFail"
          slot="required-field-date"
          class="small text-danger mt-2"
        >
          The Last enrollment is a required field
        </div>
      </BaseDatePicker>
      <BaseDatePicker
        :id-date-picker="'start'"
        :form-old-data="form"
        :form-exist-data="$_section"
        date-picker-class="w-25 mr-2"
        :disabled="hasEnrolledLearner"
        type-date="start_at"
        @emitDate="getStartDate"
      >
        <span slot="title">Start session</span>
        <div
          v-if="!form.start_at && submitFail"
          slot="required-field-date"
          class="small text-danger mt-2"
        >
          The Start is a required field
        </div>
      </BaseDatePicker>
      <BaseDatePicker
        :id-date-picker="'end-date'"
        :form-old-data="form"
        :form-exist-data="$_section"
        date-picker-class="w-25 mr-2"
        :disabled="hasEnrolledLearner"
        type-date="end_at"
        @emitDate="getEndDate"
      >
        <span slot="title">End Session</span>
        <div
          v-if="!form.end_at && submitFail"
          slot="required-field-date"
          class="small text-danger mt-2"
        >
          The End is a required field
        </div>
      </BaseDatePicker>

      <label>Number of learners</label>
      <b-form-input
        id="available-spots"
        v-model="$v.form.max_learners.$model"
        type="number"
        class="form-control w-25"
        :value="$v.form.max_learners.$model"
        :state="$v.form.max_learners.$anyError ? false : null"
      />
      <b-form-invalid-feedback id="available-spots-invalid-feedback" class="mt-2">
        <div v-if="!$v.form.max_learners.minValue">
          Number of available spots can't be less than enrolled learner.
        </div>
        <div v-else-if="!$v.form.max_learners.maxValue">
          Number of available spots can't be more than 40
        </div>
        <div v-else-if="!$v.form.max_learners.required">Number of available spots is required.</div>
      </b-form-invalid-feedback>

      <!-- Online meetings -->
      <div v-if="learningUnit && learningUnit.number_of_online_meeting == 0">
        <div class="h5 font-weight-bold mt-5">Online meetings</div>
        <div class="text-dark mb-4">This learning unit has no online meeting.</div>
      </div>

      <div v-if="learningUnit && learningUnit.number_of_online_meeting != 0">
        <div class="h5 font-weight-bold mt-5">Online meetings</div>
        <div class="text-dark mb-4">45 minutes</div>
        <b-alert :show="!isSufficientMeetings && submitFail" class="my-2" variant="warning">
          This LU requires
          {{ learningUnit ? learningUnit.number_of_online_meeting : '' }}
          online meetings but the specified timing is not sufficient to cover all meetings
        </b-alert>

        <b-alert :show="overlappedMeetings.length > 0" class="my-2" variant="danger">
          The specified meetings timing overlaps your other meetings.
          <div
            class="font-weight-bold online-meetings__show-conflicted-details"
            @click="showModal('modal-alert-overlap-meeting')"
          >
            Show details
          </div>
          <ModalAlertConflict
            ref="modal-alert-overlap-meeting"
            conflict-type="overlap-meeting"
            title="Overlapped online meetings"
            sub-title="Online meetings"
            description="The specified meeting timing overlaps your other meetings."
            :conflict-list="overlappedMeetings"
            @confirm="submit"
          />
        </b-alert>

        <b-alert :show="tooCloseMeetings.length > 0" class="my-2" variant="warning">
          The specified meeting timing creates too short gaps with your other meetings.
          <div
            class="font-weight-bold online-meetings__show-conflicted-details"
            @click="showModal('modal-alert-too-close-meeting')"
          >
            Show details
          </div>
          <ModalAlertConflict
            ref="modal-alert-too-close-meeting"
            conflict-type="too-close-meeting"
            title="Meetings with less than 1 hour break"
            description="The specified meeting timing creates too short gaps with your other meetings. It is recommended to have at least 1 hour free before or after each meeting."
            :conflict-list="tooCloseMeetings"
            :form="form"
            :hide-footer="false"
            @confirm="confirmTooClose"
          />
        </b-alert>

        <b-alert
          :show="!form.selectedDays.length && submitFail"
          class="mt-2 mb-4"
          variant="warning"
        >
          Meeting day is required.
        </b-alert>

        <div class="w-25 mb-3 mt-4">
          <label class="mb-3">Start</label>
          <b-form-select
            id="select_start_time"
            v-model="form.online_meeting_start_time"
            :disabled="hasEnrolledLearner"
            :options="hrs"
          />
        </div>

        <b-form-group v-slot="{ ariaDescribedby }" label="Day" :disabled="hasEnrolledLearner">
          <b-form-checkbox
            v-for="day in dayOfWeek"
            :id="`input-${day.toLowerCase()}`"
            :key="day"
            v-model="form.selectedDays"
            :value="day"
            :aria-describedby="ariaDescribedby"
            name="flavour-4a"
          >
            <div>
              {{ day }}
            </div>
          </b-form-checkbox>
        </b-form-group>

        <b-form-group label="Repeat" :disabled="hasEnrolledLearner">
          <b-form-radio
            v-model="form.selectedRepeat"
            class="my-2"
            name="does not repeat"
            :value="sectionRepeatNotRepeat.id"
          >
            Does not repeat
          </b-form-radio>
          <b-form-radio
            v-model="form.selectedRepeat"
            class="my-2"
            name="weekly"
            :value="sectionRepeatWeekly.id"
          >
            Weekly
          </b-form-radio>
          <b-form-radio
            v-model="form.selectedRepeat"
            class="my-2"
            name="biweekly"
            :value="sectionRepeatBiWeekly.id"
          >
            Biweekly
          </b-form-radio>
        </b-form-group>
      </div>
      <div>
        <b-form-group label="Status" :disabled="hasEnrolledLearner || conflict">
          <template #label>
            <div class="mb-2">Status</div>

            <b-alert ref="alert__post-conflict" :show="conflict" variant="warning" class="mb-0">
              Post schedule must be in the session interval
              <a
                href="javascript:void(0)"
                class="button__reschedule-post ml-1"
                @click="goToPostList()"
              >
                Reschedule the conflicting post
              </a>
            </b-alert>
          </template>

          <b-form-radio v-model="form.is_published" class="my-2" name="draft" :value="false">
            Draft
          </b-form-radio>
          <b-form-radio
            v-model="form.is_published"
            class="my-2"
            name="published"
            :value="true"
            :disabled="isDisabledPublish"
            @click.native="isDisabledPublish && notiLearningUnitMustBePublished()"
          >
            Published
          </b-form-radio>
        </b-form-group>
      </div>
      <div class="mb-5 d-flex border-top pt-2 mt-4">
        <b-button class="mr-3 h-50" variant="success" :disabled="isSubmitting" @click="submit">
          <span v-if="!isSubmitting">{{ sectionId ? 'Save' : 'Create' }}</span>

          <span v-else class="pr-2 pl-2">
            <b-spinner label="Loading..." variant="secondary" small />
          </span>
        </b-button>

        <b-button variant="link" class="btn-link-dark mr-3 h-50" @click="goBackToPreviousPage">
          Cancel
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import BaseDatePicker from './BaseDatePicker';
import LearningUnit from '@/models/LearningUnit';
import Meeting from '@/models/Meeting';
import Section from '@/models/Section';
import { notificationMixin, datetimeMixin, sectionMixin } from '@/mixins';

import moment from 'moment';
import { required, minValue, maxValue } from 'vuelidate/lib/validators';
import ModalAlertConflict from './ModalAlertConflict';
import { useDateTimeFormat, useMeetingGenerator } from '../../../composables';
import { SectionRepeat } from '@/models';

const { generateMeeting } = useMeetingGenerator();
const { isTimeOverlapped } = useDateTimeFormat();

export default {
  name: 'BaseFormSection',

  components: {
    BaseDatePicker,
    ModalAlertConflict
  },

  mixins: [notificationMixin, datetimeMixin, sectionMixin],

  props: {
    conflict: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      form: {
        start_at: null,
        start_enrollment: null,
        last_enrollment: null,
        end_at: null,
        max_learners: null,
        online_meeting_start_time: '09:00',
        selectedDays: [],
        /* 1 = Does not repeat, 2 = Weekly, 3 = Biweekly */
        selectedRepeat: null,
        is_published: false
      },
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
      isReady: false,
      isLoading: false,
      submitFail: false,
      // ownerSections: [],
      // overlappedSections: [],
      overlappedMeetings: [],
      tooCloseMeetings: [],
      isConfirmTooClose: false,
      numberOfLearners: 0,
      isSubmitting: false
    };
  },

  validations() {
    return {
      form: {
        max_learners: {
          required,
          minValue: this.numberOfLearners ? minValue(this.numberOfLearners) : minValue(1),
          maxValue: maxValue(40)
        }
      }
    };
  },

  computed: {
    hasEnrolledLearner() {
      return this.numberOfLearners > 0;
    },

    lastEnrollmentDate() {
      return this.$_section.last_enrollment;
    },

    pageType() {
      return this.$route.name.split('/')[2];
    },

    sectionId() {
      return this.$route.params.sectionId ? Number(this.$route.params.sectionId) : null;
    },

    learningUnitSections() {
      return this.learningUnit && this.learningUnit.sections;
    },

    ownerSections() {
      let ownerSections = [];

      const ownerLearningUnits = LearningUnit.query()
        .with('sections.section_repeat')
        .where((learningUnit) => learningUnit.isOwnerOrInstructor(this.userId))
        .get();

      if (ownerLearningUnits.length) {
        ownerSections = ownerLearningUnits
          .flatMap((learningUnit) => learningUnit.sections)
          .filter((section) => section);
      }

      return ownerSections;
    },

    hrs() {
      return this.generateTime(0, 48);
    },

    /**
     * Check disabled publish section
     * @return {Boolean}
     */
    isDisabledPublish() {
      return this.learningUnit && !this.learningUnit.is_published;
    },

    /**
     * Start date and end date must not diff greater than 42 days (6 weeks)
     * @return {Boolean}
     */
    isDurationGreaterThanSixWeek() {
      const dayAfterSixWeeks = moment(
        moment(this.formatedDatetime(this.form.start_at, 'LLL'), 'LLL').add(42, 'days').format()
      );

      return (
        moment
          .duration(
            dayAfterSixWeeks.diff(moment(this.formatedDatetime(this.form.end_at, 'LLL'), 'LLL'))
          )
          .asDays() < 0
      );
    },

    // /**
    //  * Check if section overlap old sections
    //  * @return {Array} sections that overlap with other sections in this learning unit
    //  */
    // sectionOverlapList() {
    //   if (!this.form.start_at || !this.form.end_at) return [];

    //   const { sections = [] } = this.learningUnit;

    //   return sections.filter(
    //     section => this.sectionId !== section.id && isTimeOverlapped(
    //       [section.start_at, section.end_at],
    //       [this.form.start_at, this.form.end_at]
    //     )
    //   );
    // },

    /**
     * Check if section overlap old sections
     * @return {Array} sections that overlap with other sections in this learning unit
     */
    overlappedSections() {
      if (!this.form.start_at || !this.form.end_at) return [];

      const { sections = [] } = this.learningUnit;

      return sections
        .filter(
          (section) =>
            this.sectionId !== section.id &&
            isTimeOverlapped(
              [section.start_at, section.end_at],
              [this.form.start_at, this.form.end_at]
            )
        )
        .map((section) => ({
          learning_unit: {
            id: this.learningUnit.id,
            name: this.learningUnit.name
          },
          section_id: section.id,
          start_at: this.formatedDatetime(section.start_at, 'MMM D, YYYY HH:mma'),
          end_at: this.formatedDatetime(section.end_at, 'MMM D, YYYY HH:mma'),
          dates: [],
          overlap: true,
          tooClose: false
        }));
    },

    selectedSectionRepeat() {
      return SectionRepeat.find(this.form.selectedRepeat);
    },

    generatedMeetings() {
      const { number_of_online_meeting = 0 } = this.learningUnit;

      let generatedMeetings = [];

      if (number_of_online_meeting > 0) {
        const meetingData = {
          start_at: this.form.start_at,
          end_at: this.form.end_at,
          meeting_repeat_type: this.selectedSectionRepeat.name,
          meeting_days: this.form.selectedDays,
          meeting_start_time: moment(this.form.online_meeting_start_time, 'HH:mm').format(
            'HH:mm:ss.sss'
          )
        };

        generatedMeetings = generateMeeting(meetingData);

        if (generatedMeetings.length) {
          generatedMeetings = generatedMeetings.splice(0, number_of_online_meeting);
        }
      }

      return generatedMeetings;
    },

    meetings() {
      let { generatedMeetings = [] } = this;

      return generatedMeetings.map((ts) => new Date(ts).toISOString());
    },

    /**
     * Number of Zoom meeting จะต้อง >= จำนวน meeting ที่ set ไว้ตอนสร้าง LU
     * Alert: This LU requires X online meetings but the specified timing is not sufficient to cover all meetings.
     * @returns {Boolean}
     */
    isSufficientMeetings() {
      const { meetings } = this;
      const { number_of_online_meeting = 0 } = this.learningUnit;

      return number_of_online_meeting > 0 ? meetings.length >= number_of_online_meeting : true;
    }
  },

  watch: {
    $_section: {
      immediate: true,
      handler(section) {
        if (section) {
          let {
            start_at,
            start_enrollment,
            last_enrollment,
            end_at,
            max_learners,
            online_meeting_start_time,
            days_of_week,
            section_repeat_id,
            is_published,
            number_of_learners
          } = section;

          const onlyHourFormat = moment(online_meeting_start_time, 'LT').format('HH:mm');

          const arrayDaysOfWeek = JSON.parse(days_of_week.replace(/'/g, '"'));

          this.form = {
            start_at,
            start_enrollment,
            last_enrollment,
            end_at,
            max_learners,
            online_meeting_start_time: onlyHourFormat,
            selectedDays: arrayDaysOfWeek,
            selectedRepeat: section_repeat_id || this.sectionRepeatNotRepeat.id,
            is_published
          };

          this.numberOfLearners = number_of_learners;
        }

        this.isReady = true;
      }
    },

    userId: {
      immediate: true,
      handler(userId) {
        if (userId) {
          this.fetchAllOwnerSections(userId);
        }
      }
    },

    ownerSections: {
      immediate: true,
      handler() {
        this.validateConflictMeetings();
      }
    },

    'form.start_at': {
      handler() {
        this.validateConflictMeetings();
      }
    },

    'form.end_at': {
      handler() {
        this.validateConflictMeetings();
      }
    },

    'form.selectedDays': {
      handler() {
        this.validateConflictMeetings();
      }
    },
    'form.online_meeting_start_time': {
      handler() {
        this.validateConflictMeetings();
      }
    },
    'form.selectedRepeat': {
      handler() {
        this.validateConflictMeetings();
      }
    }
  },

  methods: {
    /**
     * Notify user when publish section before learning unit
     */
    notiLearningUnitMustBePublished() {
      this.$toast.open({
        message: 'Learning unit of this section have to be published',
        type: 'error',
        pauseOnHover: true,
        duration: 5000
      });
    },

    /**
     * Fetch all sections were owned by user
     * @param {Number} uid unqiue id of user
     */
    async fetchAllOwnerSections(userId) {
      this.isLoading = true;

      try {
        await LearningUnit.api().fetchIsMember(userId, null, null);

        if (!this.$_section) {
          this.$set(this.form, 'selectedRepeat', this.sectionRepeatNotRepeat.id);
        }
        // this.ownerSections = data
        //   .flatMap((learningUnit) => learningUnit.sections)
        //   .filter((section) => section);
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },

    submit() {
      /**
       * last_enrollment ต้องเป็นวันที่อยู่ระหว่าง start_at และ end_at date-time
       *
       * start_at ต้องเป็นวันที่ก่อนหน้า end_at
       * */
      const {
        formatedDatetime,
        // sectionOverlapList,
        overlappedSections,
        overlappedMeetings
      } = this;

      const { start_at, end_at, start_enrollment, last_enrollment, max_learners } = this.form;

      this.$v.form.$touch();

      if (this.$v.form.$invalid) {
        this.$refs['top'].scrollIntoView({ behavior: 'smooth' });
        return;
      }
      if (!start_at || !end_at || !last_enrollment) {
        this.submitFail = true;

        return;
      }

      if (
        moment(formatedDatetime(start_enrollment, 'LLL'), 'LLL').isSameOrAfter(
          moment(formatedDatetime(last_enrollment, 'LLL'), 'LLL')
        )
      ) {
        this.notiWarningPrioritizeDatetime({
          first: 'start enrollment',
          second: 'last enrollment'
        });

        return;
      }

      if (
        moment(formatedDatetime(start_enrollment, 'LLL'), 'LLL').isSameOrAfter(
          moment(formatedDatetime(start_at, 'LLL'), 'LLL')
        )
      ) {
        this.notiWarningPrioritizeDatetime({
          first: 'start enrollment',
          second: 'start'
        });

        return;
      }

      if (
        moment(formatedDatetime(start_enrollment, 'LLL'), 'LLL').isSameOrAfter(
          moment(formatedDatetime(end_at, 'LLL'), 'LLL')
        )
      ) {
        this.notiWarningPrioritizeDatetime({
          first: 'start enrollment',
          second: 'end'
        });

        return;
      }

      if (
        moment(formatedDatetime(last_enrollment, 'LLL'), 'LLL').isSameOrAfter(
          moment(formatedDatetime(end_at, 'LLL'), 'LLL')
        )
      ) {
        this.notiWarningPrioritizeDatetime({
          first: 'last enrollment',
          second: 'end'
        });

        return;
      }

      if (
        moment(formatedDatetime(start_at, 'LLL'), 'LLL').isSameOrAfter(
          moment(formatedDatetime(end_at, 'LLL'), 'LLL')
        )
      ) {
        this.notiWarningPrioritizeDatetime({
          first: 'start',
          second: 'end'
        });

        return;
      }

      if (this.isDurationGreaterThanSixWeek) {
        this.notiWarningDurationGreaterThanSixWeeks();

        return;
      }

      if (!this.isSufficientMeetings) {
        this.submitFail = true;

        this.notiNotSufficientMeeting(this.learningUnit.number_of_online_meeting);

        return;
      }

      if (overlappedSections?.length) {
        this.submitFail = true;

        this.notiSectionOverlap();

        this.showModal('modal-alert-overlap-section');
        return;
      }

      // const meetingStartTime = moment(
      //   this.form.online_meeting_start_time,
      //   "LT"
      // ).utc().format("HH:mm:ss");

      // await Section.api().validate({
      //   userId: this.userId,
      //   sectionId: this.sectionId,
      //   start_at: this.form.start_at,
      //   end_at: this.form.end_at,
      //   meeting_repeat_type: this.selectedSectionRepeat.name,
      //   meeting_days: this.form.selectedDays,
      //   meeting_start_time: meetingStartTime,
      // });

      if (overlappedMeetings?.length) {
        this.submitFail = true;

        this.notiMeetingOverlap();

        this.showModal('modal-alert-overlap-meeting');
        return;
      }

      if (this.tooCloseMeetings.length > 0 && !this.isConfirmTooClose) {
        this.showModal('modal-alert-too-close-meeting');
        return;
      }

      if (
        start_at &&
        end_at &&
        start_enrollment &&
        last_enrollment &&
        !overlappedSections?.length &&
        !overlappedMeetings?.length &&
        max_learners !== 0
      ) {
        let payload = {
          ...this.form,
          meeting_datetimes: this.meetings,
          learning_unit: this.learningUnit.id,
          max_learners: +this.form.max_learners,
          online_meeting_start_time: moment(this.form.online_meeting_start_time, 'LT').format(
            'HH:mm:ss'
          ),
          days_of_week: JSON.stringify(this.form.selectedDays),
          section_repeat: this.form.selectedRepeat
        };

        if (this.sectionId) {
          this.submitUpdateSection(payload);
        } else {
          this.submitCreateSection(payload);
        }

        // this.$emit("emitSubmitSection", {
        //   ...this.form,
        //   meeting_datetimes: this.meetings,
        // });
      }
    },

    async submitCreateSection(form) {
      this.isSubmitting = true;

      try {
        await Section.api().createSection(form);

        this.goToInfoSectionPage();

        this.notiSuccess('Section', 'created');
      } catch (error) {
        const errorMessage = error.response.data.message;
        if (errorMessage === 'new section collapse with old section') {
          this.notiErrorDefault(
            'Cannot create session, this session has collapsed time with the other sessions in the same LU'
          );
        } else {
          this.notiError();
        }
      } finally {
        this.isSubmitting = false;
      }
    },

    async submitUpdateSection(form) {
      this.isSubmitting = true;

      try {
        Meeting.delete(({ section_id = null } = {}) => {
          return section_id == this.sectionId;
        });

        await Section.api().updateSection(this.sectionId, form);

        this.goToInfoSectionPage();

        this.notiSuccessDefault(
          'Session time successfully changed, please review the post schedule'
        );
      } catch (error) {
        const errorMessage = error.response.data.message;

        if (errorMessage === 'updated section collapse with old section') {
          this.notiErrorDefault(
            'Cannot update session, this session has collapsed time with the other sessions in the same LU'
          );
        } else {
          this.notiError();
        }
      } finally {
        this.isSubmitting = false;
      }
    },

    goToInfoSectionPage() {
      if (this.$route.query.previousPath) {
        const { previousPath } = this.$route.query;

        this.$router.push({ path: previousPath });
      } else {
        this.$router.push({
          name: 'design-planning/info',
          params: {
            learningUnitId: this.learningUnit.id
          },
          query: {
            tab: 'sessions'
          }
        });
      }
    },

    confirmTooClose() {
      this.isConfirmTooClose = true;
      this.submit();
    },

    /**
     * @returns {Object[]} overlapped and too close dates list of meetings
     */
    validateConflictMeetings() {
      const { generatedMeetings = [] } = this;

      /* Guard clause */
      if (!this.ownerSections.length || !generatedMeetings.length) return;

      // const overlapSectionsList = [];
      const overlapMeetingsList = [];
      const tooCloseMeetingsList = [];

      for (let sectionIndex = 0; sectionIndex < this.ownerSections.length; sectionIndex++) {
        const section = this.ownerSections[sectionIndex];

        const {
          learning_unit_id: LU_id,
          id: section_id,
          start_at,
          end_at,
          section_repeat_id: meeting_repeat,
          days_of_week,
          online_meeting_start_time: meeting_start_time
        } = section;

        if (this.sectionId && section_id == this.sectionId) {
          continue;
        }

        const learningUnit = LearningUnit.query().where('id', LU_id).first();

        const { name: LU_name, number_of_online_meeting: number_other_meeting } = learningUnit;

        const meeting_days = days_of_week && JSON.parse(days_of_week.replace(/'/g, '"'));

        if (isTimeOverlapped([start_at, end_at], [this.form.start_at, this.form.end_at])) {
          // const formMeetingDate = {
          //   start_at: this.form.start_at,
          //   end_at: this.form.end_at,
          //   meeting_repeat: this.form.selectedRepeat,
          //   meeting_days: this.form.selectedDays,
          //   meeting_start_time: moment(
          //     this.form.online_meeting_start_time,
          //     "HH:mm"
          //   ).format("HH:mm:ss.sss"),
          // };

          const otherMeetingDate = {
            start_at,
            end_at,
            meeting_repeat,
            meeting_days,
            meeting_start_time
          };

          /* Calculate all meetings */
          let otherMeetingsList = this.meetingsList(otherMeetingDate).filter((meeting) =>
            moment(meeting).isSameOrAfter(moment(generatedMeetings[0]), 'days')
          );

          otherMeetingsList =
            otherMeetingsList.length > number_other_meeting
              ? otherMeetingsList.slice(0, number_other_meeting)
              : otherMeetingsList;

          /* Compared form meetings with other meetings */
          const meetingData = {
            learning_unit: { id: LU_id, name: LU_name },
            section_id,
            start_at: this.formatedDatetime(start_at, 'MMM D, YYYY HH:mma'),
            end_at: this.formatedDatetime(end_at, 'MMM D, YYYY HH:mma'),
            dates: [],
            overlap: false,
            tooClose: false
          };

          generatedMeetings.forEach((formMeeting) => {
            otherMeetingsList.forEach((otherMeeting) => {
              /* overlaping meetings */
              this.checkOverlapMeetings(formMeeting, otherMeeting, meetingData);

              /* too short gap meetings */
              this.checkTooShortGapMeetings(formMeeting, otherMeeting, meetingData);
            });
          });

          /* push conflict meetings to the list when the meetings conflicted /*
          /* overlaping LU with meetings */
          const isMeetingOverlapped =
            meetingData.dates.length && meetingData.overlap ? true : false;

          if (isMeetingOverlapped) {
            overlapMeetingsList.push({
              ...meetingData
            });
          }

          /* too short gap LU with meetings */
          const isMeetingTooClose = meetingData.dates.length && meetingData.tooClose ? true : false;
          if (isMeetingTooClose) {
            tooCloseMeetingsList.push({
              ...meetingData,
              overlap: true
            });
          }
        }
      }

      /* Check overlap between sections*/
      // console.log(945, this.sectionOverlapList);
      // for (
      //   let sectionIndex = 0;
      //   sectionIndex < this.sectionOverlapList.length;
      //   sectionIndex++
      // ) {
      //   const meetingData = this.checkOverlapSections(sectionIndex);
      //   overlapSectionsList.push({ ...meetingData });
      // }

      // this.overlappedSections = [...overlapSectionsList];

      this.overlappedMeetings = [...overlapMeetingsList];

      this.tooCloseMeetings = [...tooCloseMeetingsList];
    },

    checkOverlapMeetings(formMeeting, otherMeeting, meetingData) {
      if (
        moment(formMeeting).isSame(otherMeeting, 'days') &&
        Math.abs(moment(formMeeting).diff(otherMeeting, 'minutes')) < 60
      ) {
        this.$set(meetingData, 'overlap', true);

        meetingData.dates.push(
          `${this.formatedDatetime(otherMeeting, 'ddd, MMM D, YYYY HH:mma')} ${
            this.cityTimezone
          } time`
        );
      }
    },

    checkTooShortGapMeetings(formMeeting, otherMeeting, meetingData) {
      if (
        moment(formMeeting).isSame(otherMeeting, 'days') &&
        Math.abs(moment(formMeeting).diff(otherMeeting, 'minutes')) === 60
      ) {
        this.$set(meetingData, 'tooClose', true);

        meetingData.dates.push(
          `${this.formatedDatetime(otherMeeting, 'ddd, MMM D, YYYY HH:mma')} ${
            this.cityTimezone
          } time`
        );
      }
    },

    // checkOverlapSections(sectionIndex) {
    //   const section = this.sectionOverlapList[sectionIndex];

    //   const {
    //     learning_unit_id: LU_id,
    //     id: section_id,
    //     start_at,
    //     end_at,
    //   } = section;

    //   const { name: LU_name } = this.learningUnit;

    //   const meetingData = {
    //     learning_unit: { id: LU_id, name: LU_name },
    //     section_id,
    //     start_at: this.formatedDatetime(start_at, "MMM D, YYYY HH:mma"),
    //     end_at: this.formatedDatetime(end_at, "MMM D, YYYY HH:mma"),
    //     dates: [],
    //     overlap: true,
    //     tooClose: false,
    //   };

    //   return meetingData;
    // },

    showModal(modalType) {
      this.$refs[modalType].show(modalType);
    },
    goBackToPreviousPage() {
      this.$router.go(-1);
    },
    getStartDate(start) {
      this.form.start_at = start;
    },
    getStartEnrollmentDate(start_enrollment) {
      this.form.start_enrollment = start_enrollment;
    },
    getLastEnrollmentDate(last_enrollment) {
      this.form.last_enrollment = last_enrollment;
    },
    getEndDate(end) {
      this.form.end_at = end;
    },

    goToPostList() {
      const query = {
        previousPath: this.$route.fullPath,
        view: 'list'
      };
      this.$router.push({
        name: 'design-planning/teaching-feed',
        query,
        params: {
          sectionId: this.sectionId,
          learningUnitId: this.learningUnit.id
        }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
/* Remove arrow of input type="number" */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Remove arrow of input type="number" in Firefox */
input[type='number'] {
  -moz-appearance: textfield;
}

.online-meetings {
  &__show-conflicted-details {
    cursor: pointer;
    transition: all 0.3s;

    &:hover,
    &:active {
      transform: translateY(-1px);
    }
  }
}

::v-deep div.custom-control.custom-checkbox {
  min-height: 2.5rem;
}
</style>
